import React, { useMemo, useState } from 'react';
import { CashFlowEntry } from '../types';
import { usePos } from '../context/PosContext';
import { formatTime, formatDate } from '../services/datetime';
import { printReportAuto } from '../services/receipt';
import './Sections.css';
import './CashFlow.css';

type Tab = 'expense' | 'advance' | 'credit_received' | 'received';

const TABS: { key: Tab; label: string }[] = [
  { key: 'expense', label: 'Expenses' },
  { key: 'advance', label: 'Advance' },
  { key: 'credit_received', label: 'Customer Credit' },
  { key: 'received', label: 'Received Amount' },
];

const EXPENSE_TYPES = ['General', 'Rent', 'Salary', 'Electricity', 'Supplies', 'Transport', 'Maintenance', 'Misc'];

const isToday = (iso: string) => {
  const d = new Date(iso);
  const n = new Date();
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
};

const CashFlow: React.FC = () => {
  const { cashflow, customers, addCashFlow, formatMoney, config } = usePos();
  const [tab, setTab] = useState<Tab>('expense');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [name, setName] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [expenseType, setExpenseType] = useState(EXPENSE_TYPES[0]);
  const [method, setMethod] = useState<'cash' | 'digital'>('cash');

  const selectedCustomer = customers.find((c) => c.id === customerId);

  const resetForm = () => {
    setAmount('');
    setNote('');
    setName('');
    setCustomerId('');
  };

  const save = () => {
    const amt = parseFloat(amount) || 0;
    if (amt <= 0) return;
    const customer = customers.find((c) => c.id === customerId);
    addCashFlow({
      type: tab,
      amount: amt,
      customerId: customerId || undefined,
      customerName: customer?.name ?? (name || undefined),
      note: tab === 'expense' ? `${expenseType}${note ? ' · ' + note : ''}` : note || undefined,
      paymentMethod: tab === 'expense' ? method : 'cash',
    });
    resetForm();
  };

  const todays = useMemo(
    () => cashflow.filter((e) => e.type === tab && isToday(e.timestamp)).reverse(),
    [cashflow, tab]
  );
  const todayTotal = todays.reduce((s, e) => s + e.amount, 0);

  // Print today's list for the active tab to a thermal printer (ESC/POS),
  // falling back to the browser print dialog.
  const printToday = () => {
    const label = TABS.find((t) => t.key === tab)?.label ?? 'CashFlow';
    const rows: (string | number)[][] = todays.map((e) => [
      formatTime(e.timestamp),
      e.customerName ?? '-',
      e.note ?? '-',
      formatMoney(e.amount),
    ]);
    rows.push(['', '', 'TOTAL', formatMoney(todayTotal)]);
    printReportAuto(`CashFlow — ${label}`, `${config.storeName} · ${formatDate(new Date())}`, ['Time', 'Party', 'Note', 'Amount'], rows);
  };

  return (
    <div className="section-page">
      <div className="page-header">
        <h1>CashFlow</h1>
      </div>

      {/* segmented tab bar (payment_layout.xml) */}
      <div className="cf-tabs">
        {TABS.map((t) => (
          <button key={t.key} className={`cf-tab ${tab === t.key ? 'active' : ''}`} onClick={() => { setTab(t.key); resetForm(); }}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="cf-body">
        {/* form */}
        <div className="section-card cf-form">
          {tab === 'expense' && (
            <>
              <div className="cf-field">
                <label>Expense Type :</label>
                <select value={expenseType} onChange={(e) => setExpenseType(e.target.value)}>
                  {EXPENSE_TYPES.map((x) => (
                    <option key={x}>{x}</option>
                  ))}
                </select>
              </div>
              <div className="cf-field">
                <label>Payment Mode :</label>
                <div className="cf-seg">
                  <button className={method === 'cash' ? 'on' : ''} onClick={() => setMethod('cash')}>
                    {config.currencySymbol} Cash
                  </button>
                  <button className={method === 'digital' ? 'on' : ''} onClick={() => setMethod('digital')}>
                    {config.currencySymbol} Digital
                  </button>
                </div>
              </div>
            </>
          )}

          {tab === 'advance' && (
            <div className="cf-field">
              <label>Emp / Party Name :</label>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Enter name" />
            </div>
          )}

          {(tab === 'credit_received' || tab === 'received') && (
            <div className="cf-field">
              <label>Cust Name :</label>
              <select value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
                <option value="">Select Customer</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {tab === 'credit_received' && (
            <div className="cf-field">
              <label>Balance :</label>
              <input readOnly value={selectedCustomer ? formatMoney(selectedCustomer.creditBalance || 0) : 'Old Balance'} />
            </div>
          )}

          <div className="cf-field">
            <label>Amount :</label>
            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Enter Amount" />
          </div>

          <div className="cf-field">
            <label>Note :</label>
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Enter Note" />
          </div>

          <div className="cf-actions">
            <button className="btn-teal" onClick={save}>
              SAVE
            </button>
            <button className="btn-grey" onClick={printToday}>
              PRINT
            </button>
          </div>
        </div>

        {/* today's list */}
        <div className="section-card cf-list">
          <div className="cf-list-head">
            <h2>Today's List</h2>
            <span className="cf-total">{formatMoney(todayTotal)}</span>
          </div>
          {todays.length === 0 ? (
            <p className="muted">No entries today.</p>
          ) : (
            <table className="section-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Party</th>
                  <th>Note</th>
                  <th className="right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {todays.map((e) => (
                  <tr key={e.id}>
                    <td>{formatTime(e.timestamp)}</td>
                    <td>{e.customerName ?? '—'}</td>
                    <td>{e.note ?? '—'}</td>
                    <td className="right" style={{ fontWeight: 700, color: tab === 'expense' ? 'var(--color-accent-dark)' : 'var(--color-primary-dark)' }}>
                      {formatMoney(e.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default CashFlow;
